import React from "react";
import { useState, useEffect, useRef } from "react";

import ReCAPTCHA from "react-google-recaptcha";

import SEO from "../components/seo";

const C = {
  forest: "#1B3A2D",
  forestDeep: "#132B21",
  emerald: "#059669",
  emeraldLight: "#D1FAE5",
  amber: "#F59E0B",
  amberDark: "#D97706",
  amberGlow: "#FEF3C7",
  warm: "#FFFBEB",
  white: "#FFFFFF",
  offWhite: "#F9FAFB",
  gray100: "#F3F4F6",
  gray300: "#D1D5DB",
  gray500: "#6B7280",
  gray700: "#374151",
  gray900: "#111827",
};

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function Tag({ children, color = C.emerald }) {
  return (
    <span style={{
      display: "inline-block",
      background: `${color}18`,
      color,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 1.8,
      textTransform: "uppercase",
      padding: "7px 18px",
      borderRadius: 40,
      border: `1px solid ${color}30`,
    }}>
      {children}
    </span>
  );
}

function Tick() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="10" cy="10" r="10" fill={C.emerald} />
      <path d="M6 10.5L9 13.5L14 7.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function InventivPatentLanding() {
  const captchaRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [isCaptchaInvalid, setIsCaptchaInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [focused, setFocused] = useState(null);
  const seo = {
    metaDesc: 'ProvisionalBuilder - Inventiv.org'
  };
  const submit = async () => {
    if (form.name.trim() && form.email.trim() && form.phone.trim()) {
      const token = captchaRef.current?.getValue?.();
      if (!token) {
        setIsCaptchaInvalid(true);
      } else {
        setIsCaptchaInvalid(false);

        // 
        const data = {
          client: 'inventiv.org',
          name: form.name,
          email: form.email,
          phone: form.phone,
          recaptchaToken: token,
        };
        // submit
        try {
          setIsSubmitting(true);
          const response = await fetch("https://api.trustyip.com/subcribers/add", {
            "method": "POST",
            "headers": {
              "content-type": "application/json",
              "accept": "application/json"
            },
            "body": JSON.stringify(data)
          });

          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }

          // Some success responses can be empty/plain-text, so parse JSON only when available.
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            await response.json().catch(() => null);
          }

          // Reset captcha before toggling success UI to avoid ref being unmounted.
          captchaRef.current?.reset?.();
          // Keep email for success message, only clear name and phone.
          setForm(prev => ({ ...prev, name: "", phone: "" }));
          setCaptchaToken("");
          setDone(true);
        } catch (err) {
          alert("There is something wrong. Please try again later!");
          console.log(err);
        } finally {
          setIsSubmitting(false);
        }
      }
    }

  };

  const valid = form.name.trim() && form.email.trim() && form.phone.trim() && captchaToken;

  const inp = (field) => ({
    width: "100%",
    padding: "14px 16px",
    fontSize: 15,
    fontFamily: "'Libre Franklin', sans-serif",
    border: `2px solid ${focused === field ? C.emerald : "rgba(255,255,255,0.2)"}`,
    borderRadius: 10,
    outline: "none",
    background: "rgba(255,255,255,0.07)",
    color: C.white,
    transition: "border-color 0.25s, box-shadow 0.25s, background 0.25s",
    boxShadow: focused === field ? `0 0 0 4px ${C.emerald}22` : "none",
    boxSizing: "border-box",
  });

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "rgba(255,255,255,0.7)",
    marginBottom: 6,
  };

  return (
    <>
      <SEO title="ProvisionalBuilder - Inventiv.org" canonical='/provisional-builder' seo={seo} />
      <div style={{ fontFamily: "'Libre Franklin', sans-serif", color: C.gray900, overflowX: "hidden" }}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&display=swap" rel="stylesheet" />

        {/* ===== HERO WITH FORM ===== */}
        <section style={{
          position: "relative",
          background: `linear-gradient(165deg, ${C.forest} 0%, ${C.forestDeep} 55%, #0a1f16 100%)`,
          padding: "0 24px",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: -150, right: -100, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(5,150,105,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -100, left: -60, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(245,158,11,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />

          <div style={{
            maxWidth: 1140,
            margin: "0 auto",
            width: "100%",
            padding: "100px 0 80px",
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 400px",
            gap: 56,
            alignItems: "center",
          }}>
            {/* LEFT — Headline & Benefits */}
            <div>
              <FadeIn>
                <Tag color={C.amber}>Inventiv Foundation Exclusive</Tag>
              </FadeIn>

              <FadeIn delay={0.08}>
                <h1 style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "clamp(30px, 4vw, 50px)",
                  fontWeight: 900,
                  color: C.white,
                  lineHeight: 1.1,
                  marginTop: 24,
                  marginBottom: 20,
                }}>
                  We're Covering Half the Cost of Your{" "}
                  <span style={{
                    color: C.amber,
                    textDecoration: "underline",
                    textDecorationColor: `${C.amber}50`,
                    textUnderlineOffset: 6,
                    textDecorationThickness: 3,
                  }}>Provisional Patent Drafting</span>
                </h1>
              </FadeIn>

              <FadeIn delay={0.14}>
                <p style={{
                  fontSize: "clamp(15px, 1.6vw, 18px)",
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.7,
                  marginBottom: 32,
                  maxWidth: 560,
                }}>
                  Inventiv Foundation has partnered with PowerPatent — the AI-powered drafting platform built by patent lawyers — and <strong style={{ color: C.amber }}>we're personally sponsoring a 50% discount</strong> so you can protect your invention without breaking the bank.
                </p>
              </FadeIn>

              <FadeIn delay={0.22}>
                <div style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  gap: 12,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 14,
                  padding: "24px 28px",
                }}>
                  {[
                    "Inventiv is bearing the full cost of this exclusive offer",
                    "AI-assisted drafting — complete first draft in minutes",
                    "Created by experienced patent attorneys + generative AI",
                    "Ideal for startups, inventors, and IP professionals",
                    "Save significant time and money vs. traditional drafting",
                  ].map((t, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
                      <Tick />
                      <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14.5, lineHeight: 1.5 }}>{t}</span>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>

            {/* RIGHT — Form Card */}
            <FadeIn delay={0.18}>
              <div style={{
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: 20,
                padding: "36px 32px",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                position: "relative",
                overflow: "hidden",
              }}>
                {/* Top accent */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg, ${C.emerald} 0%, ${C.amber} 100%)`,
                }} />

                {!done ? (
                  <>
                    <div style={{ textAlign: "center", marginBottom: 28 }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: "50%",
                        background: "rgba(5,150,105,0.15)", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        margin: "0 auto 14px", fontSize: 24,
                      }}>🎟️</div>
                      <h2 style={{
                        fontFamily: "'Fraunces', serif",
                        fontSize: 24,
                        fontWeight: 800,
                        color: C.white,
                        lineHeight: 1.2,
                        marginBottom: 8,
                      }}>
                        Claim Your 50% Discount
                      </h2>
                      <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.5, margin: 0 }}>
                        Quick & free — get your exclusive Inventiv code instantly.
                      </p>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div>
                        <label style={labelStyle}>
                          Full Name <span style={{ color: "#f87171" }}>*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Jane Smith"
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          onFocus={() => setFocused("name")}
                          onBlur={() => setFocused(null)}
                          style={inp("name")}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Email Address <span style={{ color: "#f87171" }}>*</span>
                        </label>
                        <input
                          type="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={e => setForm({ ...form, email: e.target.value })}
                          onFocus={() => setFocused("email")}
                          onBlur={() => setFocused(null)}
                          style={inp("email")}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          onFocus={() => setFocused("phone")}
                          onBlur={() => setFocused(null)}
                          style={inp("phone")}
                        />
                      </div>
                      <div>
                        <ReCAPTCHA
                          sitekey={process.env.RECAPTCHA_SITE_KEY}
                          ref={captchaRef}
                          onChange={token => setCaptchaToken(token || "")}
                          onExpired={() => setCaptchaToken("")}
                        />
                        {isCaptchaInvalid && (
                          <div style={{ color: "#f87171", fontSize: 13, marginTop: 6 }}>
                            Please complete the CAPTCHA to proceed.
                          </div>
                        )}
                      </div>

                      <button
                        onClick={submit}
                        disabled={!valid || isSubmitting}
                        style={{
                          width: "100%",
                          padding: "16px 20px",
                          fontSize: 15,
                          fontWeight: 700,
                          fontFamily: "'Libre Franklin', sans-serif",
                          color: C.forestDeep,
                          background: !valid || isSubmitting ? "rgba(255,255,255,0.25)" : `linear-gradient(135deg, ${C.amber} 0%, #fbbf24 100%)`,
                          border: "none",
                          borderRadius: 10,
                          cursor: !valid || isSubmitting ? "not-allowed" : "pointer",
                          boxShadow: !valid || isSubmitting ? "none" : "0 4px 24px rgba(245,158,11,0.35)",
                          transition: "all 0.25s ease",
                          marginTop: 2,
                          letterSpacing: 0.2,
                          opacity: isSubmitting ? 0.9 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 10,
                        }}
                      >
                        {isSubmitting && (
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              border: `2px solid ${C.forestDeep}66`,
                              borderTopColor: C.forestDeep,
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {isSubmitting ? "Submitting..." : "Get Discount For Provisional Patent Drafting"}
                      </button>

                      <p style={{
                        fontSize: 11.5,
                        color: "rgba(255,255,255,0.4)",
                        lineHeight: 1.55,
                        textAlign: "center",
                        margin: 0,
                      }}>
                        You'll receive your exclusive <strong style={{ color: "rgba(255,255,255,0.6)" }}>Inventiv</strong> coupon code instantly via email, plus a direct link to apply it on PowerPatent.com. No credit card required. <strong style={{ color: "rgba(255,255,255,0.6)" }}>Limited spots — act fast!</strong>
                      </p>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "12px 0" }}>
                    <div style={{
                      width: 68, height: 68, borderRadius: "50%",
                      background: "rgba(5,150,105,0.15)", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      margin: "0 auto 20px", fontSize: 30,
                    }}>✅</div>
                    <h3 style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: 24, fontWeight: 800, color: C.white,
                      marginBottom: 12,
                    }}>
                      You're All Set, {form.name.split(" ")[0]}!
                    </h3>
                    <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.65, marginBottom: 20 }}>
                      Check your inbox at <strong style={{ color: C.white }}>{form.email}</strong> for your Inventiv-sponsored discount code.
                    </p>
                    {/* <div style={{
                    background: "rgba(245,158,11,0.12)",
                    borderRadius: 12,
                    padding: "20px 20px",
                    border: `1px solid rgba(245,158,11,0.25)`,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", marginBottom: 6, letterSpacing: 1 }}>YOUR COUPON CODE</div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: C.white, letterSpacing: 5, fontFamily: "'Libre Franklin', sans-serif" }}>INVENTIV</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>Apply at PowerPatent.com checkout for 50% off</div>
                  </div> */}
                  </div>
                )}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ===== PARTNERSHIP STORY ===== */}
        <section style={{ background: C.warm, padding: "100px 24px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <FadeIn>
              <div style={{ textAlign: "center", marginBottom: 56 }}>
                <Tag>Our Mission</Tag>
                <h2 style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "clamp(26px, 4vw, 42px)",
                  fontWeight: 800,
                  color: C.forest,
                  marginTop: 20,
                  marginBottom: 20,
                  lineHeight: 1.2,
                }}>
                  Making Patent Protection Accessible for Everyone
                </h2>
                <p style={{ fontSize: 18, color: C.gray500, maxWidth: 620, margin: "0 auto", lineHeight: 1.75 }}>
                  At Inventiv Foundation, we believe great ideas deserve protection — regardless of budget. That's why we've partnered with a leading AI patent drafting platform and are <strong style={{ color: C.forest }}>personally covering the cost</strong> to bring you this 50% discount.
                </p>
              </div>
            </FadeIn>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 24,
            }}>
              {[
                {
                  step: "01",
                  icon: "🤝",
                  title: "We Partnered Up",
                  desc: "Inventiv Foundation negotiated an exclusive partnership with PowerPatent's AI drafting platform on your behalf.",
                },
                {
                  step: "02",
                  icon: "💰",
                  title: "We're Covering the Cost",
                  desc: "This isn't a marketing gimmick — Inventiv is sponsoring the full 50% discount from our own funds so you pay less.",
                },
                {
                  step: "03",
                  icon: "🎟️",
                  title: "You Get the Code",
                  desc: "Claim your exclusive coupon code above. Apply it at checkout on PowerPatent for an instant 50% off — no strings attached.",
                },
              ].map((card, i) => (
                <FadeIn key={i} delay={i * 0.1}>
                  <div style={{
                    background: C.white,
                    borderRadius: 16,
                    padding: "36px 28px",
                    height: "100%",
                    border: `1px solid ${C.gray100}`,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    position: "relative",
                    transition: "transform 0.3s, box-shadow 0.3s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.07)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; }}
                  >
                    <div style={{
                      position: "absolute", top: 20, right: 24,
                      fontSize: 48, fontWeight: 900, color: `${C.emerald}12`,
                      fontFamily: "'Fraunces', serif", lineHeight: 1,
                    }}>{card.step}</div>
                    <div style={{ fontSize: 36, marginBottom: 16 }}>{card.icon}</div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: C.forest, marginBottom: 10 }}>{card.title}</h3>
                    <p style={{ fontSize: 15, color: C.gray500, lineHeight: 1.65, margin: 0 }}>{card.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ===== WHAT YOU GET ===== */}
        <section style={{ background: C.white, padding: "100px 24px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 56,
              alignItems: "center",
            }}>
              <FadeIn>
                <div>
                  <Tag>What You Get</Tag>
                  <h2 style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: "clamp(26px, 3.5vw, 38px)",
                    fontWeight: 800,
                    color: C.forest,
                    marginTop: 20,
                    marginBottom: 20,
                    lineHeight: 1.2,
                  }}>
                    Professional-Grade Provisional Patent Drafting — at Half the Price
                  </h2>
                  <p style={{ fontSize: 17, color: C.gray500, lineHeight: 1.75, marginBottom: 28 }}>
                    Through our partnership, you'll access an AI-powered drafting platform that turns your invention descriptions into complete, review-ready provisional patent applications. Here's what's included:
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {[
                      { title: "Complete First Drafts", sub: "Generate comprehensive provisional applications from your descriptions, claims, and figures" },
                      { title: "Attorney-Grade Quality", sub: "Built by experienced patent attorneys — the AI ensures consistency, clarity, and strength" },
                      { title: "Minutes, Not Weeks", sub: "Go from idea to a protected IP asset dramatically faster than traditional drafting" },
                      { title: "Half Price, Full Value", sub: "Thanks to Inventiv's sponsorship, you pay 50% less without compromising on quality" },
                    ].map((item, i) => (
                      <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <Tick />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: C.forest, marginBottom: 3 }}>{item.title}</div>
                          <div style={{ fontSize: 14, color: C.gray500, lineHeight: 1.55 }}>{item.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={0.12}>
                <div style={{
                  background: `linear-gradient(155deg, ${C.forest} 0%, ${C.forestDeep} 100%)`,
                  borderRadius: 20,
                  padding: "44px 36px",
                  color: C.white,
                  position: "relative",
                  overflow: "hidden",
                }}>
                  <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(245,158,11,0.06)", pointerEvents: "none" }} />

                  <div style={{
                    display: "flex", alignItems: "center", gap: 12, marginBottom: 28,
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "14px 18px",
                  }}>
                    <div style={{ fontSize: 28 }}>🏛️</div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>Sponsored By</div>
                      <div style={{ fontSize: 17, fontWeight: 700 }}>Inventiv Foundation</div>
                    </div>
                  </div>

                  <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 800, marginBottom: 12, lineHeight: 1.25 }}>
                    Who Is This For?
                  </h3>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: "rgba(255,255,255,0.75)", marginBottom: 24 }}>
                    If you're part of the Inventiv community — an independent inventor, startup founder, or IP professional — this discount was arranged specifically for you.
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                      "Independent inventors protecting a breakthrough",
                      "Startups building an IP portfolio to attract investors",
                      "IP professionals accelerating their workflow",
                    ].map((t, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14.5, color: "rgba(255,255,255,0.85)" }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.amber, flexShrink: 0 }} />
                        {t}
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: 28,
                    background: `${C.amber}20`,
                    border: `1px solid ${C.amber}40`,
                    borderRadius: 12,
                    padding: "16px 20px",
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 4, letterSpacing: 1 }}>YOUR EXCLUSIVE CODE</div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: C.white, letterSpacing: 6, fontFamily: "'Libre Franklin', sans-serif" }}>INVENTIV</div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 6 }}>50% off at PowerPatent checkout</div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ===== WHY PROVISIONAL PATENTS ===== */}
        <section style={{ background: C.offWhite, padding: "90px 24px" }}>
          <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
            <FadeIn>
              <Tag>Why File a Provisional Patent?</Tag>
              <h2 style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(24px, 3.5vw, 36px)",
                fontWeight: 800,
                color: C.forest,
                marginTop: 20,
                marginBottom: 20,
                lineHeight: 1.25,
              }}>
                Secure Your Priority Date Without the Full Patent Cost
              </h2>
              <p style={{ fontSize: 17, color: C.gray500, lineHeight: 1.75, maxWidth: 640, margin: "0 auto 40px" }}>
                A provisional patent application gives you a 12-month window to test, refine, and develop your invention — all while establishing your legal filing date. It's the smartest first step for any inventor.
              </p>
            </FadeIn>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 20,
              textAlign: "left",
            }}>
              {[
                { emoji: "📅", title: "12-Month Priority", desc: "Lock in your filing date while you continue developing and testing your invention." },
                { emoji: "💵", title: "Fraction of the Cost", desc: "Significantly cheaper than a full non-provisional patent application — especially with our 50% discount." },
                { emoji: "🧪", title: "Test Before You Commit", desc: "Use the provisional period to validate your idea's market potential before investing in a full patent." },
                { emoji: "🛡️", title: "Legal Protection", desc: "Establishes \"patent pending\" status, giving you recourse if someone tries to copy your invention." },
              ].map((c, i) => (
                <FadeIn key={i} delay={i * 0.08}>
                  <div style={{
                    background: C.white,
                    borderRadius: 14,
                    padding: "28px 24px",
                    height: "100%",
                    border: `1px solid ${C.gray100}`,
                  }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>{c.emoji}</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: C.forest, marginBottom: 8 }}>{c.title}</div>
                    <div style={{ fontSize: 14, color: C.gray500, lineHeight: 1.6 }}>{c.desc}</div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <section style={{
          background: C.forest,
          padding: "44px 24px",
          textAlign: "center",
        }}>
          <div style={{ maxWidth: 650, margin: "0 auto" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.white, marginBottom: 12, fontFamily: "'Fraunces', serif" }}>
              Inventiv Foundation
            </div>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginBottom: 20 }}>
              Making innovation protection affordable. This exclusive 50% discount on AI-powered provisional patent drafting is fully sponsored by Inventiv Foundation for our community of inventors and entrepreneurs.
            </p>
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "8px 28px",
              marginBottom: 20,
            }}>
              {[
                "Offer Sponsored by Inventiv Foundation",
                "Drafting Platform: PowerPatent (powerpatent.com)",
                "Protected by Multiple US Patents",
              ].map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.emerald, flexShrink: 0 }} />
                  {t}
                </div>
              ))}
            </div>
            <div style={{ width: 50, height: 1, background: "rgba(255,255,255,0.1)", margin: "0 auto 16px" }} />
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.28)", margin: 0 }}>
              © {new Date().getFullYear()} Inventiv Foundation, Inc. All rights reserved.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
